import { UnionToIntersection } from 'utils/utiltyTypes/utilityTypes'

export type CamelEntityType = (typeof typeTranslations)[number]['camel'] // TODO: Rename this when the migration of types casing is over
export type CamelSystemCoreDataType = (typeof systemCoreDataTranslations)[number]['camel'] // TODO: Rename this when the migration of types casing is over
export type AnyEntityType = keyof EntityTypeTranslationObject
export type AnyDependantEntityType = keyof SystemCoreDataTranslationObject // Excludes supplier-profile (things that CAN have dependencies)
export type AnyDependencyEntityType = keyof EntityTypeTranslationObject // Includes supplier-profile (things that can BE dependencies)
export const isAnyEntityType = (type: unknown): type is AnyEntityType =>
  typeof type === 'string' && type in typeTranslationsObject
export const isAnyDependantEntityType = (type: unknown): type is AnyDependantEntityType =>
  typeof type === 'string' && type in systemCoreDataTranslationsObject
export const isAnyDependencyEntityType = (type: unknown): type is AnyDependencyEntityType =>
  typeof type === 'string' && type in typeTranslationsObject
export const isAnySystemCoreDataType = (type: unknown): type is CamelSystemCoreDataType =>
  typeof type === 'string' && type in systemCoreDataTranslationsObject
/**
 * Given an entity type in any format, translate it to the desired format.
 */
export const formatEntityType = <Type extends keyof EntityTypeTranslationObject, Format extends keyof EntityTypeObject>(
  type: Type,
  to: Format
): EntityTypeTranslationObject[Type][Format] => {
  // Throw a descriptive error for any mistypings/castings of "type"
  if (!(type in typeTranslationsObject)) {
    throw new Error(`Unknown entity type: "${type}" (JS type: ${typeof type})`)
  }

  return typeTranslationsObject[type][to]
}

const systemCoreDataTranslations = [
  // Address
  {
    dynamicOptionSource: 'addresses',
    camel: 'address',
    camelPlural: 'addresses',
    kebab: 'address',
    snake: 'address',
    snakePlural: 'addresses',
  },

  // Currency
  {
    dynamicOptionSource: 'currencies',
    camel: 'currency',
    camelPlural: 'currencies',
    kebab: 'currency',
    snake: 'currency',
    snakePlural: 'currencies',
  },

  // Department
  {
    dynamicOptionSource: 'department', // The only one that is not plural (╯°□°)╯︵ ┻━┻
    camel: 'department',
    camelPlural: 'departments',
    kebab: 'department',
    snake: 'department',
    snakePlural: 'departments',
  },

  // Line item type
  {
    dynamicOptionSource: 'line-item-types',
    camel: 'lineItemType',
    camelPlural: 'lineItemTypes',
    kebab: 'line-item-type',
    snake: 'line_item_type',
    snakePlural: 'line_item_types',
  },

  // Payment method
  {
    dynamicOptionSource: 'payment-methods',
    camel: 'paymentMethod',
    camelPlural: 'paymentMethods',
    kebab: 'payment-method',
    snake: 'payment_method',
    snakePlural: 'payment_methods',
  },

  // Payment term
  {
    dynamicOptionSource: 'payment-terms',
    camel: 'paymentTerm',
    camelPlural: 'paymentTerms',
    kebab: 'payment-term',
    snake: 'payment_term',
    snakePlural: 'payment_terms',
  },

  // Subsidiary
  {
    dynamicOptionSource: 'subsidiaries',
    camel: 'subsidiary',
    camelPlural: 'subsidiaries',
    kebab: 'subsidiary',
    snake: 'subsidiary',
    snakePlural: 'subsidiaries',
  },
] as const satisfies EntityTypeObject[]

const typeTranslations = [
  ...systemCoreDataTranslations,

  // Supplier profile
  {
    dynamicOptionSource: 'supplier-profiles',
    camel: 'supplierProfile',
    camelPlural: 'supplierProfiles',
    kebab: 'supplier-profile',
    snake: 'supplier_profile',
    snakePlural: 'supplier_profiles',
  },
] as const satisfies EntityTypeObject[]

// 👇 HELPERS

interface EntityTypeObject {
  dynamicOptionSource: string
  camel: string
  camelPlural: string
  kebab: string
  snake: string
  snakePlural: string
}

type SingleMap<T extends EntityTypeObject> = T[keyof T] extends `${infer Value}` ? Record<Value, T> : Record<never, never>

const buildSingleMap = <T extends EntityTypeObject>(object: T): SingleMap<T> => {
  return Object.values(object).reduce(
    (acc, value) => {
      acc[value] = object
      return acc
    },
    {} as Record<string, T>
  ) as SingleMap<T>
}

type Map<T extends EntityTypeObject> = UnionToIntersection<T extends EntityTypeObject ? SingleMap<T> : Record<never, never>>

const buildMap = <T extends EntityTypeObject>(mappingObjects: readonly T[]): Map<T> => {
  return Object.assign({}, ...mappingObjects.map(buildSingleMap))
}

const typeTranslationsObject = buildMap(typeTranslations)
const systemCoreDataTranslationsObject = buildMap(systemCoreDataTranslations)
type EntityTypeTranslationObject = typeof typeTranslationsObject
type SystemCoreDataTranslationObject = typeof systemCoreDataTranslationsObject

export const isSystemCoreDataType = (type: string): type is keyof SystemCoreDataTranslationObject => {
  return type in systemCoreDataTranslationsObject
}
